// The JSON the API (../web) serves, endpoint by endpoint (docs/api.md), for
// web to answer with and the site to read: a field renamed on one side is a
// type error on the other. Times are unix seconds; figures are published
// ones (app_stats, app_ranks), so a ranked app has them all.

import type { CompanyRole } from "./companies.ts";
import type { ServedHealth } from "./health.ts";
import type { Family } from "./tags.ts";

export type { CompanyRole };

// The platforms a game's store page can list.
export type Platform = "windows" | "mac" | "linux";

// A game's store details as the API serves them (GET /api/apps/:id's
// `store`): the appdetails fields the game page shows, as ../crawler reads
// them from Steam's AppData and tracked_apps.store_info stores them (JSON).
// Text is trimmed and unescaped; anything missing or malformed is null or
// empty.
export interface StoreInfo {
  description: string | null;
  developers: string[];
  publishers: string[];
  // As Steam writes it ("Aug 3, 2023", "Coming soon", "Q1 2027").
  releaseDate: string | null;
  comingSoon: boolean;
  // Steam's is_free. Missing from store info fetched before it was read.
  isFree?: boolean;
  genres: string[];
  platforms: Platform[];
  metacritic: { score: number; url: string | null } | null;
  website: string | null;
}

// Any non-200 answer.
export interface ErrorResponse {
  error: string;
}

// A paged list's paging fields.
export interface Paging {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

// A tag's value as served (tagValue): its state, its score as `value`, and
// its data's fields.
export interface TagValue {
  state?: string;
  value?: number;
  [field: string]: unknown;
}

// A tag on an app: its family (null for a tag no longer in the registry).
export interface AppTag {
  family: Family | null;
  tag: string;
  value: TagValue;
}

// An app's published figures. day_change is the 24h change as a ratio
// (0.25 is +25%), null without a day-ago count or from zero.
export interface Stats {
  player_count: number;
  recorded_at: number;
  peak_player_count: number;
  day_ago_player_count: number | null;
  day_ago_recorded_at: number | null;
  day_change: number | null;
  rank: number;
  day_ago_rank: number | null;
}

// The figures of an app that may have none: not published yet, or (rank)
// not in the latest hour.
export type MaybeStats = { [K in keyof Stats]: Stats[K] | null };

// An app as a list row, with its tags with states. `trend`, on the lists
// that draw it, is its average players on each of the last whole UTC days.
export interface AppRowBase {
  app_id: number;
  name: string | null;
  header_image: string | null;
  tags: AppTag[];
  trend?: number[];
}

// A ranked app's row: the home list, a tag's list, the apps beside one.
export type RankedRow = AppRowBase & Stats;

// A row of a list that can hold unranked or inactive apps: search, a company.
export type ActiveRow = AppRowBase & MaybeStats & { active: boolean };

// A row of a tag's list: the app, its tag's value, and when its tag last
// changed.
export type TaggedRow = RankedRow & { value: TagValue; computed_at: number };

// GET /api/apps
export interface AppsResponse extends Paging {
  apps: RankedRow[];
  as_of: number | null;
}

// GET /api/trending
export interface TrendingResponse {
  apps: RankedRow[];
  min_players: number;
}

// GET /api/tags
export interface TagIndexResponse {
  families: Record<
    string,
    {
      every: string;
      tags: Record<string, { about: string; states: string[] | null; filters: string[] }>;
    }
  >;
}

// GET /api/tags/<tag>
export interface TagResponse extends Paging {
  tag: string;
  family: Family;
  every: string;
  apps: TaggedRow[];
}

// GET /api/movers: each movers tag's states, and the home page's other cards
// (../shared/site.ts's HOME_TAGS), each its strongest apps.
export interface MoversResponse {
  tags: Record<string, Record<string, TaggedRow[]>>;
}

// How a developer labelled an update on Steam, as app_updates.kind stores it:
// ../crawler reads it from Steam's events, and the site names each.
export type UpdateKind = "major" | "regular" | "small";

// Why an update has no change: see latestUpdates in ../web.
export type ImpactReason = "untracked" | "too_soon" | "missing_days";

// One of an app's latest updates, with how its players moved.
export interface UpdateImpact {
  t: number;
  title: string;
  gid: string;
  kind: string;
  before: number | null;
  after: number | null;
  change: number | null;
  days_in: number | null;
  reason: ImpactReason | null;
  ready_at: number | null;
}

// One of an app's companies, with some of its other ranked games.
export interface AppCompany {
  role: CompanyRole;
  slug: string;
  name: string;
  total: number;
  apps: RankedRow[];
}

// An app's US store price now (GET /api/apps/:id's `price`), in cents:
// `since`, when it last changed, and `lowest`, the lowest it has sold for
// since prices were first checked, and when it first did.
export interface Price {
  initial: number;
  final: number;
  discount: number;
  since: number;
  lowest: number;
  lowest_at: number;
}

// GET /api/apps/:id
export type AppResponse = MaybeStats & {
  app_id: number;
  name: string | null;
  active: boolean;
  header_image: string | null;
  peak_recorded_at: number | null;
  tracking_since: number | null;
  ranked_total: number | null;
  as_of: number | null;
  // null until the crawler has fetched the app's store details.
  store: StoreInfo | null;
  // null when the store sells it for no price (free, or not out yet), or its
  // price was never checked.
  price: Price | null;
  tags: AppTag[];
  nearby: RankedRow[];
  updates: UpdateImpact[];
  companies: AppCompany[];
};

// The ranges GET /api/apps/:id/history takes, shortest first, as the site
// offers them, and the resolution each is served at.
export const HISTORY_RANGES = ["24h", "7d", "30d", "90d", "1y", "all"] as const;
export type HistoryRange = (typeof HISTORY_RANGES)[number];

export const isHistoryRange = (value: string | null): value is HistoryRange =>
  value !== null && (HISTORY_RANGES as readonly string[]).includes(value);

export type Resolution = "hour" | "day" | "week";

// An hourly point, and a daily or weekly one (its range of hourly counts).
export interface HourPoint {
  t: number;
  player_count: number;
}

export interface RangePoint {
  t: number;
  min: number;
  avg: number;
  max: number;
}

// An update announcement on the chart.
export interface UpdateMark {
  t: number;
  title: string;
  gid: string;
  kind: string;
}

// A sale on the chart: from the hour its discount was first seen (`t`) to
// the hour it was gone (`end`, null while it's on), at `discount` percent
// off the full price, in US cents. A sale whose discount changed partway is
// one mark per discount.
export interface SaleMark {
  t: number;
  end: number | null;
  discount: number;
  initial: number;
  final: number;
}

// GET /api/apps/:id/history: hourly points at "hour", daily or weekly ones
// otherwise, so checking `resolution` says which `points` are.
export type HistoryResponse = {
  app_id: number;
  range: HistoryRange;
  since: number;
  updates: UpdateMark[];
  sales: SaleMark[];
} & (
  | { resolution: "hour"; points: HourPoint[] }
  | { resolution: Exclude<Resolution, "hour">; points: RangePoint[] }
);

// GET /api/companies/<role>/<slug>
export interface CompanyResponse extends Paging {
  role: CompanyRole;
  slug: string;
  name: string;
  ranked: number;
  players: number;
  other_role: number;
  as_of: number | null;
  apps: ActiveRow[];
}

// GET /api/search
export interface SearchResponse extends Paging {
  apps: ActiveRow[];
}

// GET /api/health
export type HealthResponse = ServedHealth;
